import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { callAPI } from '../../../../services/api.service';

// Detect-and-fix tools for legacy fulfillment data. Scans change nothing; fixes are explicit
// and idempotent. Server: /admin/fulfillment-repair/*.

interface ParkedCopy {
  ownerEmail: string;
  orderNumber: string;
  fulfillmentStatus: string | null;
}
interface ParkedRelease {
  parentOrderNumber: string;
  parentOwnerEmail: string;
  classroomName: string | null;
  parkedCopies: ParkedCopy[];
}
interface CounterStatus {
  isRunning: boolean;
  dryRun: boolean;
  scannedOrders: number;
  checkedBooks: number;
  driftCount: number;
  appliedCount: number;
  preview: Array<{ bookId: string; title: string; purchased: number; pending: number; shippedBefore: number; shippedAfter: number }>;
  previewTruncated: boolean;
  errors: string[];
  finishedAt: string | null;
}

const FulfillmentRepair: React.FC = () => {
  // Parked releases
  const [scanning, setScanning] = useState(false);
  const [healing, setHealing] = useState(false);
  const [parked, setParked] = useState<{ releases: ParkedRelease[]; scannedParents: number; truncated: boolean } | null>(null);

  // Counter drift
  const [counterStatus, setCounterStatus] = useState<CounterStatus | null>(null);
  const [starting, setStarting] = useState(false);

  const fetchCounterStatus = async () => {
    const response = await callAPI('admin/fulfillment-repair/shipped-counters/status', null, 'GET');
    if (response?.data) setCounterStatus(response.data);
  };

  useEffect(() => {
    fetchCounterStatus();
    const interval = setInterval(() => {
      if (counterStatus?.isRunning) fetchCounterStatus();
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counterStatus?.isRunning]);

  const handleScanParked = async () => {
    setScanning(true);
    try {
      const response = await callAPI('admin/fulfillment-repair/parked-releases/scan', {});
      if (response?.releases) {
        setParked(response);
        toast.success(
          response.releases.length
            ? `${response.releases.length} release(s) with parked copies found`
            : 'No parked release copies — nothing to heal',
        );
      } else {
        toast.error(response?.error || 'Scan failed');
      }
    } finally {
      setScanning(false);
    }
  };

  const handleHealParked = async () => {
    if (!window.confirm('Heal all parked release copies? This re-applies the provider stamp (idempotent, no duplicate shipments).')) return;
    setHealing(true);
    try {
      const response = await callAPI('admin/fulfillment-repair/parked-releases/heal', {});
      if (typeof response?.healedOrders === 'number') {
        toast.success(`Healed ${response.healedOrders} order(s) across ${response.releasesTouched} release(s)`);
        (response.errors || []).forEach((e: string) => toast.error(e));
        handleScanParked();
      } else {
        toast.error(response?.error || 'Heal failed');
      }
    } finally {
      setHealing(false);
    }
  };

  const handleStartCounters = async (apply: boolean) => {
    if (apply && !window.confirm('Apply counter fixes? Run a dry run first and review the drift list.')) return;
    setStarting(true);
    try {
      const response = await callAPI('admin/fulfillment-repair/shipped-counters/start', { apply });
      response?.ok ? toast.success(response.message) : toast.error(response?.message || response?.error || 'Start failed');
      fetchCounterStatus();
    } finally {
      setStarting(false);
    }
  };

  const parkedTotal = parked?.releases.reduce((n, r) => n + r.parkedCopies.length, 0) || 0;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-lg font-semibold mb-1">Fulfillment Repair</h2>
      <p className="text-sm text-gray-500 mb-5">
        Detect-and-fix tools for legacy data. Scans change nothing; fixes are explicit and safe to re-run.
      </p>

      {/* Tool 1: parked release copies */}
      <div className="border rounded-lg p-4 mb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold text-sm">Parked release copies</h3>
            <p className="text-xs text-gray-500 max-w-2xl">
              Class releases sent before the fix could leave a buyer-side order copy stuck as "waiting for release"
              (shows Preparing forever, trips the stuck alarm) even though the box shipped. Scan finds them; Heal
              re-applies the provider stamp, which un-parks every copy. Shipped/canceled orders are never touched.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              onClick={handleScanParked}
              disabled={scanning || healing}
              className="bg-storyblue text-white rounded px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
            >
              {scanning ? 'Scanning…' : 'Scan'}
            </button>
            {parkedTotal > 0 && (
              <button
                type="button"
                onClick={handleHealParked}
                disabled={healing || scanning}
                className="bg-amber-600 text-white rounded px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
              >
                {healing ? 'Healing…' : `Heal ${parkedTotal} order(s)`}
              </button>
            )}
          </div>
        </div>
        {parked && (
          <div className="mt-3 text-sm">
            {parked.releases.length === 0 ? (
              <span className="text-green-700">✓ Clean — no parked copies in the last {parked.scannedParents} release(s).</span>
            ) : (
              <div className="max-h-48 overflow-y-auto border rounded divide-y">
                {parked.releases.map((r) => (
                  <div key={r.parentOrderNumber} className="px-3 py-1.5">
                    <span className="font-mono">#{r.parentOrderNumber}</span>
                    {r.classroomName ? ` — ${r.classroomName}` : ''} · {r.parkedCopies.length} parked:
                    <span className="font-mono text-gray-500"> {r.parkedCopies.map((c) => c.orderNumber).join(', ')}</span>
                  </div>
                ))}
              </div>
            )}
            {parked.truncated && (
              <p className="text-xs text-amber-600 mt-1">Only the newest {parked.scannedParents} releases were scanned — re-run after healing to walk further back.</p>
            )}
          </div>
        )}
      </div>

      {/* Tool 2: shipped-counter drift */}
      <div className="border rounded-lg p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold text-sm">Shipped-counter drift</h3>
            <p className="text-xs text-gray-500 max-w-2xl">
              Teachers' "waiting to ship" is purchased − shipped per book; old bugs let those drift, showing phantom
              waiting books. Dry run recomputes from the real pending pool and lists what would change; Apply writes
              the corrected counters.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              onClick={() => handleStartCounters(false)}
              disabled={starting || counterStatus?.isRunning}
              className="bg-storyblue text-white rounded px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
            >
              {counterStatus?.isRunning && counterStatus.dryRun ? 'Scanning…' : 'Dry run'}
            </button>
            <button
              type="button"
              onClick={() => handleStartCounters(true)}
              disabled={starting || counterStatus?.isRunning || !counterStatus?.finishedAt || counterStatus?.driftCount === 0}
              title={!counterStatus?.finishedAt ? 'Run a dry run first' : counterStatus?.driftCount === 0 ? 'No drift detected' : undefined}
              className="bg-amber-600 text-white rounded px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
            >
              {counterStatus?.isRunning && !counterStatus.dryRun ? 'Applying…' : 'Apply fixes'}
            </button>
          </div>
        </div>
        {counterStatus && (counterStatus.isRunning || counterStatus.finishedAt) && (
          <div className="mt-3 text-sm space-y-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><span className="text-gray-500">Status:</span> {counterStatus.isRunning ? `Running (${counterStatus.dryRun ? 'dry' : 'apply'})` : 'Finished'}</div>
              <div><span className="text-gray-500">Orders scanned:</span> {counterStatus.scannedOrders}</div>
              <div><span className="text-gray-500">Books checked:</span> {counterStatus.checkedBooks}</div>
              <div>
                <span className="text-gray-500">Drift:</span> {counterStatus.driftCount}
                {!counterStatus.dryRun && ` (fixed ${counterStatus.appliedCount})`}
              </div>
            </div>
            {counterStatus.preview.length > 0 && (
              <div className="max-h-48 overflow-y-auto border rounded divide-y font-mono text-xs">
                {counterStatus.preview.map((p) => (
                  <div key={p.bookId} className="px-3 py-1">
                    {p.bookId} "{p.title}" — purchased {p.purchased}, pending {p.pending}: shipped {p.shippedBefore} → {p.shippedAfter}
                  </div>
                ))}
              </div>
            )}
            {counterStatus.previewTruncated && <p className="text-xs text-amber-600">Preview capped at 100 — total drift is {counterStatus.driftCount}.</p>}
            {!counterStatus.isRunning && counterStatus.finishedAt && counterStatus.driftCount === 0 && (
              <span className="text-green-700">✓ Clean — all counters match the pending pool.</span>
            )}
            {counterStatus.errors.length > 0 && (
              <div className="text-red-600 text-xs">{counterStatus.errors.map((e, i) => <div key={i}>{e}</div>)}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FulfillmentRepair;
